/**
 * The Founding Provider offer, in one place (spec section 09).
 *
 * Why this module exists: the provider confirmation email must state the
 * benefit "precisely and in the same words as the terms page" (spec section
 * 17). The terms page copy is human-supplied and not written yet (CLAUDE.md
 * hard rule 2), so the two cannot be kept in sync by hand across a marketing
 * page, an email, and a legal document that does not exist yet. They are kept
 * in sync by all three reading these strings.
 *
 * What this is: the approved **offer** copy from spec section 09, reproduced as
 * written. It is marketing and program description, not legal prose, and
 * nothing here may grow into legal prose. The terms page still needs a human to
 * write the section that governs the program; when it does, it renders
 * `statement` and `benefitCovers` verbatim rather than paraphrasing them
 * (see `src/client/content/legal.ts`).
 *
 * Rules for anything added here:
 *
 * - No launch date. The window closes on an event — the marketplace opening to
 *   horse owners — and that is deliberate (spec section 09).
 * - No counts, no cap, no scarcity. There is no headcount cap in V1.
 * - Eligibility is not entitlement. Signing up reserves a spot; the benefit is
 *   granted at profile claim, after launch, once the business is verified
 *   (CLAUDE.md hard rule 4).
 *
 * This module is in `src/shared` because both halves of the app read it and
 * neither owns it. It imports nothing.
 */

export interface FoundingOfferStep {
  label: string;
  title: string;
  body: string;
}

export const FOUNDING_OFFER = {
  /**
   * Spec section 09, "The offer". Reproduced exactly. This is the sentence the
   * provider confirmation email and the terms page must both carry, word for
   * word — there is a contract test asserting the email contains it.
   */
  statement:
    'Join during early access and reserve Founding Provider status plus the upgraded marketplace profile with no recurring profile fee. Claim your profile when the Maryland network opens to horse owners to activate it.',

  /**
   * Spec section 09, "What the benefit covers". The third line is as important
   * as the first two: it is the boundary of what was promised.
   *
   * These three stay formal and contraction-free on purpose. They sit directly
   * against the locked statement and are read as part of it. The third is a
   * negative definition, which `brand-voice.md` forbids everywhere except
   * exactly here, where the boundary is the point.
   */
  benefitCovers: [
    'Founding Provider badge and status on your profile.',
    'The upgraded marketplace profile tier as defined by the standard profile capabilities at launch, with no recurring profile fee.',
    'Future paid products and services are not included.',
  ],

  /**
   * The two-step model (spec section 09). Providers experience this as "your
   * spot's reserved, finish your profile at launch". It is stated plainly and
   * never as something conditional or grudging.
   *
   * `label` and `title` are separate fields because the providers page renders
   * them as separate elements. Everywhere they are flattened into prose — the
   * terms page and both provider emails — they are joined by a period, never a
   * dash (`brand-voice.md`, punctuation).
   */
  steps: [
    {
      label: 'Step one, today',
      title: 'Join and reserve your status',
      body: 'You tell me who you are, what you do, and where you work. That reserves your Founding Provider status. Nothing to pay and nothing to set up.',
    },
    {
      label: 'Step two, at launch',
      title: 'Claim and complete your profile',
      body: "When the Maryland network opens to horse owners, I invite you to claim your profile and finish it. That's when your Founding Provider status and upgraded profile go live.",
    },
  ] as readonly FoundingOfferStep[],

  /** The window is event-based, not dated, and has no cap (spec section 09). */
  windowNote:
    "Early access stays open until the Maryland network opens to horse owners. There's no headcount cap.",

  /** Where the program is governed, once a human has written that page. */
  termsPath: '/terms',
} as const;
